import isNil from 'lodash/isNil';
import min from 'lodash/min';
import max from 'lodash/max';
import omitBy from 'lodash/omitBy';

import { IOffer } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';

const createOfferSchema = (offer: IOffer) => {
  return {
    '@type': 'Offer',
    availability: offer?.availability,
    price: offer?.price,
    priceCurrency: offer?.priceCurrency,
    itemCondition: offer?.itemCondition,
    ...(offer?.gmcSku && { sku: offer?.gmcSku }),
  };
};

const getOfferRange = (offers: IOffer[]) => {
  const prices = offers?.map((offer) => Number.parseFloat(offer.price));
  const high = max(prices) || 0;
  const low = min(prices) || 0;

  return {
    high,
    low,
  };
};

export const getProductTemplateSchema = (productTemplate: IProductTemplate, offers: IOffer[]) => {
  const offerRange = getOfferRange(offers);
  const availability =
    offerRange.high > 0 && offerRange.low > 0
      ? 'http://schema.org/InStock'
      : 'http://schema.org/OutOfStock';

  if (availability === 'http://schema.org/OutOfStock') {
    return null;
  }

  return JSON.stringify(
    omitBy(
      {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name: productTemplate?.name,
        image: [
          productTemplate?.mainPictureUrl,
          ...((productTemplate?.robotAssets &&
            productTemplate.robotAssets.map((robot) => robot.url)) ||
            []),
        ],
        releaseDate: productTemplate?.releaseDate,
        brand: {
          '@type': 'Brand',
          name: productTemplate?.brandName,
        },
        model: productTemplate?.name,
        sku: productTemplate?.sku,
        color: productTemplate?.details,
        description: productTemplate?.story || null,
        offers: offers
          ? {
              '@type': 'AggregateOffer',
              availability,
              highPrice: Number(offerRange.high).toFixed(2),
              lowPrice: Number(offerRange.low).toFixed(2),
              priceCurrency: offers?.[0]?.priceCurrency || 'USD',
              offerCount: offers?.length,
              offers: offers?.map((offer) => createOfferSchema(offer)),
            }
          : null,
      },
      isNil,
    ),
  );
};
